import React, { useState, useEffect } from "react";
import api from "../../utils/api";
import { Button } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import DataTable from "../DataTable";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import SearchKeyword from "./SearchKeyword";
import KeywordCategoryTable from "./KeywordCategoryTable";
import CreateKeywordCategoryModal from "./CreateKeywordCategoryModal";
import KeywordsForm from "../KeywordsForm";

const KeywordCategoryMain = ({
  render,
  setRender,
  data,
  rows,
  setPassSelectedCategoryId,
  columns,
  onAssignToKeyword,
}) => {
  const [rowsData, setRowsData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("all");

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [delKeywordIds, setDelKeywordIds] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(1);

  useEffect(() => {
    setRowsData(rows);
    loadColumnVisibility();
    fetchCategories();
  }, [render]);

  useEffect(() => {
    handleCategorySelect(selectedCategoryId);
  }, [render]);

  const loadColumnVisibility = () => {
    const stored = localStorage.getItem("keywordCategoryColumnVisibility");
    if (stored) setColumnVisibilityModel(JSON.parse(stored));
  };

  const saveColumnVisibility = (model) => {
    localStorage.setItem(
      "keywordCategoryColumnVisibility",
      JSON.stringify(model)
    );
    setColumnVisibilityModel(model);
  };

  // Fetch all keyword categories
  const fetchCategories = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_BASE_URL}keyword-category/all`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setCategories(res.data);
    } catch (err) {
      toast.error("Failed to fetch keyword categories");
    }
  };

  // Fetch keywords under selected category
  const fetchKeywordsByCategory = async (categoryId, page = 1) => {
    try {
      const URL = `${process.env.REACT_APP_BASE_URL}keyword-category/${categoryId}/keywords?page=${page}&limit=100`;

      const res = await api.get(URL, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setTotalItems(res.data.totalItems);
      setTotalPages(res.data.totalPages);
      setCurrentPage(res.data.currentPage);

      const flatten = res.data.keywords.map((k) => ({
        id: k._id,
        _id: k._id,
        keyword: k.keyword,
      }));

      setRowsData(flatten);
    } catch (err) {
      toast.error("Failed to fetch keywords");
    }
  };

  // Searching from SearchKeyword component
  const setSearchKeyword = (keywordData) => {
    setTotalItems(keywordData.totalItems);

    const flatten = keywordData.keywords.map((k) => ({
      id: k._id,
      _id: k._id,
      keyword: k.keyword,
    }));
    setRowsData(flatten);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchKeywordsByCategory(selectedCategoryId, newPage);
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategoryId(categoryId);
    setPassSelectedCategoryId(categoryId);
    setCurrentPage(1);
    fetchKeywordsByCategory(categoryId, 1);
  };

  // Bulk delete keywords
  const handleBulkDelete = async (deleteIds) => {
    try {
      const res = await api.post(
        `${process.env.REACT_APP_BASE_URL}keyword/bulkdelete`,
        { ids: deleteIds },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success(res.data.message);
      setRender((prev) => !prev);
    } catch (err) {
      toast.error("Failed to delete keywords");
    }
  };

  const openDeleteConfirm = (ids) => {
    setDelKeywordIds(ids);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    setIsDeleteModalOpen(false);
    handleBulkDelete(delKeywordIds);
  };

  // Delete category
  const handleDeleteCategory = async (categoryId) => {
    try {
      const res = await api.delete(
        `${process.env.REACT_APP_BASE_URL}keyword-category/delete/${categoryId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success(res.data.message);
      setRender((prev) => !prev);
    } catch (err) {
      toast.error("Failed to delete category");
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "15px 0px",
        }}
      >
        <SearchKeyword setSearchKeyword={setSearchKeyword} />
        <KeywordsForm render={render} setRender={setRender} />
      </div>
      <div style={{ display: "flex", width: "100%" }}>
        {/* Left panel: Categories */}
        <div style={{ flex: 2, marginRight: "20px" }}>
          <KeywordCategoryTable
            categories={categories}
            onCategorySelect={handleCategorySelect}
            handleDeleteCategory={handleDeleteCategory}
          />

          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              style={{
                width: "130px",
                height: "50px",
                padding: "10px",
                marginTop: "15px",
                backgroundColor: "#1B3C73",
                color: "white",
              }}
            >
              Category
            </Button>
          </div>
        </div>

        {/* Right panel: Keywords table */}
        <div style={{ flex: 10 }}>
          {rowsData.length > 0 && (
            <DataTable
              rows={rowsData}
              columns={columns}
              onDeleteSelected={openDeleteConfirm}
              onAssignToPBS={onAssignToKeyword}
              handlePageChange={handlePageChange}
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              saveColumnVisibility={saveColumnVisibility}
              columnVisibilityModel={columnVisibilityModel}
            />
          )}
        </div>
      </div>

      {/* Category Create Modal */}
      <CreateKeywordCategoryModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        fetchCategories={fetchCategories}
      />

      <ToastContainer autoClose={3000} />

      {/* Confirm delete */}
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete these Keywords?"
      />
    </div>
  );
};

export default KeywordCategoryMain;
